import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Template, TemplateApiResponse, TemplateProcessResponse } from '../models/template.model';
import { NavigationContextService } from '../../core/services/navigation-context.service';

@Injectable({
  providedIn: 'root',
})
export class TemplateRegistryService {
  private http = inject(HttpClient);
  private navigationContext = inject(NavigationContextService);
  private apiUrl = `${environment.backofficeApiUrl}/api/v1/templates`;

  /**
   * Get all templates
   * @param domainType Optional domain type filter (e.g., 'SUPPLIERS')
   * @param subDomainType Optional sub-domain type filter (e.g., 'CONTRACTS', 'ATTO_DI_NOMINA')
   */
  getTemplates(domainType?: string, subDomainType?: string): Observable<TemplateApiResponse> {
    let url = this.apiUrl;
    const params: string[] = [];
    
    if (domainType) {
      params.push(`domainType=${domainType}`);
    }
    if (subDomainType) {
      params.push(`subDomainType=${subDomainType}`);
    }
    
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    console.log('📡 TemplateRegistryService - Calling API:', url);
    return this.http.get<TemplateApiResponse>(url);
  }

  /**
   * Get templates for contract documents
   * Automatically filters by domainType based on current navigation context
   */
  getContractTemplates(): Observable<TemplateApiResponse> {
    const domainType = this.navigationContext.isSupplierContext() ? 'SUPPLIERS' : 'PROFESSIONALS';
    return this.getTemplates(domainType, 'CONTRACTS');
  }

  /**
   * Get templates for Atto di Nomina documents
   * Automatically filters by domainType based on current navigation context
   */
  getAttoDiNominaTemplates(): Observable<TemplateApiResponse> {
    const domainType = this.navigationContext.isSupplierContext() ? 'SUPPLIERS' : 'PROFESSIONALS';
    return this.getTemplates(domainType, 'ATTO_DI_NOMINA');
  }

  /**
   * Get template by ID
   */
  getTemplate(id: string | number): Observable<TemplateApiResponse> {
    return this.http.get<TemplateApiResponse>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create new template
   */
  createTemplate(template: Template): Observable<TemplateApiResponse> {
    return this.http.post<TemplateApiResponse>(this.apiUrl, template);
  }

  /**
   * Update template (PUT - full update)
   */
  updateTemplatePut(id: string | number, template: Template): Observable<TemplateApiResponse> {
    return this.http.put<TemplateApiResponse>(`${this.apiUrl}/${id}`, template);
  }

  /**
   * Delete template
   */
  deleteTemplate(id: string | number): Observable<TemplateApiResponse> {
    return this.http.delete<TemplateApiResponse>(`${this.apiUrl}/${id}`);
  }

  /**
   * Process template with context data to generate merged HTML
   * @param id Template ID
   * @param contextId Context identifier (e.g., contract ID or action ID)
   * @param contextType Context type: 'CONTRACT' or 'ACTION'
   */
  processTemplate(id: string | number, contextId: string, contextType: 'CONTRACT' | 'ACTION'): Observable<TemplateProcessResponse> {
    return this.http.post<TemplateProcessResponse>(`${this.apiUrl}/${id}/process`, { contextId, contextType });
  }

  // UTILITY - Map templates data for display
  // =========================
  
  /**
   * Traduce il tipo dominio in italiano
   */
  private translateDomainType(domainType: string): string {
    const translations: Record<string, string> = {
      'SUPPLIERS': 'Fornitori',
      'SUPPLIER': 'Fornitori', // Fallback per retrocompatibilità
      'PROFESSIONALS': 'Fiduciari',
      'FIDUCIARY': 'Fiduciari',
      'TRUSTEES': 'Fiduciari',
      'CONTRACTS': 'Contratti',
      'RESPONSIBLES': 'Responsabili',
      'DOCUMENTS': 'Documenti'
    };
    return translations[domainType] || domainType;
  }

  /**
   * Traduce il tipo sottodominio in italiano
   */
  private translateSubDomainType(subDomainType: string): string {
    const translations: Record<string, string> = {
      'CONTRACTS': 'Contratto',
      'CONTRACT': 'Contratto', // Fallback per retrocompatibilità
      'ATTO_DI_NOMINA': 'Atto di Nomina'
    };
    return translations[subDomainType] || subDomainType;
  }

  /**
   * Mappa i dati dei template per la visualizzazione nella tabella
   * Centralizza la logica per evitare duplicazioni nei componenti
   */
  mapTemplatesForDisplay(templates: any[]): any[] {
    return templates.map((template: any) => ({
      id: template.id || '',
      name: template.name || '',
      description: template.description || '',
      domainType: this.translateDomainType(template.domainType || ''),
      subDomainType: this.translateSubDomainType(template.subDomainType || ''),
      createdAt: template.createdAt ? new Date(template.createdAt).toLocaleDateString('it-IT') : '',
    }));
  }

  /**
   * Estrae l'array di template dalla risposta API
   * Gestisce diversi formati di risposta (paginata, array, singolo oggetto)
   */
  extractTemplatesArray(response: TemplateApiResponse): any[] {
    if (!response?.status || !response?.data) {
      return [];
    }

    let dataArray: any[] = [];
    
    // Check if data has content property (paginated response)
    if ('content' in response.data) {
      dataArray = Array.isArray(response.data.content) ? response.data.content : [response.data.content];
    } else if (Array.isArray(response.data)) {
      dataArray = response.data;
    } else {
      dataArray = [response.data];
    }

    return dataArray;
  }
}
